import { PostgrestResponse } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { 
  ActivityType, 
  SleepSession, 
  SleepSessionWithJoins,
  NapSession,
  NapSessionWithJoins,
  FeedingSession,
  FeedingSessionWithJoins,
  FeedingSessionRow,
  ReferenceItem,
  SleepSessionInsert,
  SleepSessionUpdate
} from '@/types/activity';
import { formatToLocalTime } from '@/lib/utils/date';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

// Initialize dayjs plugins
dayjs.extend(utc);

// Export the types that are needed elsewhere
// Re-export for backward compatibility
export type { 
  SleepSession, 
  NapSession, 
  FeedingSession, 
  FeedingSessionWithJoins,
  FeedingSessionRow,
  SleepSessionWithJoins, 
  NapSessionWithJoins,
  ReferenceItem,
  ActivityType
};

/**
 * Helper function to get the table name from the activity type
 */
function getTableNameByActivityType(type: 'sleep' | 'nap' | 'feeding'): string {
  switch (type) {
    case 'sleep':
      return 'sleep_sessions';
    case 'nap':
      return 'nap_sessions';
    case 'feeding':
      return 'feeding_sessions';
    default:
      throw new Error(`Invalid activity type: ${type}`);
  }
}

/**
 * Get sleep records for a specific child
 */
export async function getChildSleepRecords(
  childId: string,
  userId?: string,
  options?: {
    timeRange?: { start: string; end: string };
    searchQuery?: string;
  }
): Promise<SleepSessionWithJoins[]> {
  console.log('[activity-service] Getting sleep records for child:', childId);
  
  try {
    // Start building the query
    let query = supabase
      .from('sleep_sessions')
      .select(`
        *,
        onset_methods:onset_method_id(id, method),
        restfulness:restfulness_id(id, rating),
        sound:sound_id(id, name),
        sound_level:sound_level_id(id, level),
        location:location_id(id, description),
        environment:environment_id(id, description)
      `)
      .eq('child_id', childId)
      .order('end_time', { ascending: false }); 
    
    // Apply time range filter if provided
    // Use overlap logic: activity overlaps if it started before range ends AND ended after range starts
    if (options?.timeRange) {
      const { start, end } = options.timeRange;
      query = query
        .lt('start_time', end)
        .gt('end_time', start);
    }
    
    // Apply search filter if provided
    if (options?.searchQuery) {
      const searchQuery = options.searchQuery.toLowerCase();
      // Postgres ilike for case-insensitive search
      query = query.or(`notes.ilike.%${searchQuery}%`);
    }
    
    // Apply user filter if provided (for caregiver accounts)
    if (userId) {
      query = query.eq('caregiver_id', userId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching sleep records:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.log('No sleep records found for child:', childId);
      return [];
    }
    
    // Process the data and return it
    console.log(`Found ${data.length} sleep records for child:`, childId);
    
    // Type assertion to ensure compatibility
    return data as unknown as SleepSessionWithJoins[];
  } catch (error) {
    console.error('Error in getChildSleepRecords:', error);
    throw error;
  }
}

/**
 * Get sleep session data for a specific child
 */
export const fetchSleepSessionsQuery = (
  childId: string,
  limit?: number,
  timeRange?: { start: string, end: string },
  searchQuery?: string,
) => ({
  queryKey: ['childSleepRecords', childId, limit, timeRange, searchQuery],
  queryFn: async () => {
    console.log('📊 FETCHING SLEEP SESSIONS', { childId, timeRange, searchQuery });
    try {
      const records = await getChildSleepRecords(childId, undefined, { timeRange, searchQuery });
      
      console.log(`📊 SLEEP RECORDS COUNT: ${records.length}`);
      
      if (records.length === 0) {
        console.log('📊 NO SLEEP SESSIONS FOUND');
      }
      
      // Map DB records to UI-friendly format
      const results = records.map(mapSleepSessionToActivity);
      
      return results;
    } catch (error) {
      console.error('📊 ERROR FETCHING SLEEP SESSIONS', error);
      throw error;
    }
  }
});

/**
 * Fetch nap sessions for a child from Supabase
 */
export async function fetchNapSessions(childId: string, options?: {
  timeRange?: { start: string; end: string };
  searchQuery?: string;
}) {
  const { timeRange, searchQuery } = options || {};
  
  // Log query params for debugging
  console.log('🔍 [fetchNapSessions] Query params:', { childId, timeRange, searchQuery });
  
  let query = supabase
    .from('nap_sessions')
    .select(`
      *,
      sleep_onset_method:onset_method_id(id),
      restfulness_rating:restfulness_id(id),
      location:location_id(id,description),
      sleep_environment:environment_id(id,description)
    `)
    .eq('child_id', childId)
    .order('start_time', { ascending: false });
    
  // Apply time range filter if provided
  // Use overlap logic: activity overlaps if it started before range ends AND ended after range starts
  if (timeRange) {
    query = query.lt('start_time', timeRange.end).gt('end_time', timeRange.start);
  }
  
  // Apply search filter if provided
  if (searchQuery) {
    query = query.ilike('notes', `%${searchQuery}%`);
  }
  
  console.log('🔍 NAP SESSIONS QUERY', { childId, timeRange });
  const response = await query;
  
  // Log the raw response data to verify all the joins were included
  if (response.data && response.data.length > 0) {
    console.log('🔍 NAP SESSIONS RESPONSE SAMPLE:', {
      first_record: response.data[0],
      joins: {
        sleep_onset_method: response.data[0].sleep_onset_method,
        restfulness_rating: response.data[0].restfulness_rating,
        location: response.data[0].location,
        sleep_environment: response.data[0].sleep_environment
      }
    });
  } else {
    console.log('🔍 NAP SESSIONS RESPONSE: No records found');
  }
  
  return response;
}

/**
 * Fetch feeding sessions for a child from Supabase
 */
async function fetchFeedingSessions(childId: string, options?: {
  timeRange?: { start: string; end: string };
  searchQuery?: string;
}): Promise<PostgrestResponse<any>> {
  const { timeRange, searchQuery } = options || {};
  
  let query = supabase
    .from('feeding_sessions')
    .select(`
      *,
      child:child_id(id, name),
      breastfeeding:breastfeeding_sessions(
        id, 
        left_duration, 
        right_duration, 
        feeding_order,
        session_id
      ),
      bottle_sessions(
        id,
        amount_oz,
        amount_ml,
        feeding_duration,
        session_id
      ),
      formula_sessions(
        id,
        amount_oz,
        amount_ml,
        brand,
        prepared_by,
        session_id
      )
    `)
    .eq('child_id', childId)
    .order('start_time', { ascending: false });
    
  // Apply time range filter if provided
  // Use overlap logic: activity overlaps if it started before range ends AND ended after range starts
  if (timeRange) {
    query = query.lt('start_time', timeRange.end).gt('end_time', timeRange.start);
  }
  
  // Apply search filter if provided
  if (searchQuery) {
    query = query.ilike('notes', `%${searchQuery}%`);
  }
  
  console.log('🔍 FEEDING SESSIONS QUERY', { childId, timeRange });
  return await query;
}

/**
 * Custom hook to query sleep sessions for a child
 * Uses React Query for state management and caching
 */
export function useSleepSessionsQuery(
  childId: string,
  {
    timeRange,
    limit = 10,
    searchQuery,
    enabled = true
  }: {
    timeRange?: { start: string; end: string };
    limit?: number;
    searchQuery?: string;
    enabled?: boolean;
  } = {}
) {
  return useQuery({
    queryKey: ['sleepSessions', childId, timeRange, searchQuery, limit],
    enabled: !!childId && enabled,
    queryFn: async () => {
      console.log('📊 FETCHING SLEEP SESSIONS', { childId, timeRange, searchQuery });
      try {
        const records = await getChildSleepRecords(childId, undefined, { timeRange, searchQuery });
        
        console.log(`📊 SLEEP RECORDS COUNT: ${records.length}`);
        
        // Process the records - map them to ActivityType before returning
        const results = records.map(record => mapSleepSessionToActivity(record));
        return results;
      } catch (error) {
        console.error('Error in useSleepSessionsQuery:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Custom hook to query nap sessions for a child
 * Uses React Query for state management and caching
 */
export function useNapSessionsQuery(
  childId: string,
  {
    timeRange,
    searchQuery,
    enabled = true,
    queryOptions = {}
  }: {
    timeRange?: { start: string; end: string };
    searchQuery?: string;
    enabled?: boolean;
    queryOptions?: Omit<UseQueryOptions<any[], Error, ActivityType[], any>, 'queryKey' | 'queryFn'>;
  } = {}
) {
  return useQuery({
    queryKey: ['napSessions', childId, timeRange, searchQuery],
    queryFn: async () => {
      console.log('📊 FETCHING NAP SESSIONS', { childId, timeRange, searchQuery });
      try {
        const { data, error } = await fetchNapSessions(childId, {
          timeRange,
          searchQuery
        });
        
        if (error) {
          console.error('📊 NAP SESSIONS ERROR', error);
          throw error;
        }
        
        console.log(`📊 NAP RECORDS COUNT: ${data?.length || 0}`);
        
        if (!data || data.length === 0) {
          console.log('📊 NO NAP SESSIONS FOUND for childId:', childId);
          return [];
        }
        
        // Log first record for debugging
        if (data.length > 0) {
          console.log('📊 FIRST NAP RECORD SAMPLE:', {
            id: data[0]?.id,
            child_id: data[0]?.child_id,
            start_time: data[0]?.start_time,
            end_time: data[0]?.end_time,
            joined_fields: data[0]
          });
        }
        
        // Map the records to activities with safe type checking
        const results = data.map(record => {
          // First create a safe version of the record with proper null-checking for joins
          const safeRecord = {
            ...record,
            // Safely handle each joined field
            onset_method_id: record?.onset_method_id && 
              typeof record.onset_method_id === 'object' ? 
              record.onset_method_id : null,
              
            restfulness_id: record?.restfulness_id && 
              typeof record.restfulness_id === 'object' ? 
              record.restfulness_id : null,
              
            location_id: record?.location_id && 
              typeof record.location_id === 'object' ? 
              record.location_id : null,
              
            environment_id: record?.environment_id && 
              typeof record.environment_id === 'object' ? 
              record.environment_id : null
          };
          
          // Now map the safe record to an activity
          return mapNapSessionToActivity(safeRecord as NapSessionWithJoins);
        });
        
        console.log('📊 MAPPED NAP ACTIVITIES:', results.length);
        
        return results;
      } catch (error) {
        console.error('📊 ERROR FETCHING NAP SESSIONS', error);
        throw error;
      }
    },
    staleTime: 60000, // 1 minute
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...queryOptions
  });
}

/**
 * Custom hook to query feeding sessions for a child
 * Uses React Query for state management and caching
 */
export function useFeedingSessionsQuery(
  childId: string,
  {
    timeRange,
    searchQuery,
    enabled = true,
    queryOptions = {}
  }: {
    timeRange?: { start: string; end: string };
    searchQuery?: string;
    enabled?: boolean;
    queryOptions?: Omit<UseQueryOptions<any[], Error, ActivityType[], any>, 'queryKey' | 'queryFn'>;
  } = {}
) {
  return useQuery({
    queryKey: ['feedingSessions', childId, timeRange, searchQuery],
    queryFn: async () => {
      console.log('📊 FETCHING FEEDING SESSIONS', { childId, timeRange, searchQuery });
      try {
        const { data, error } = await fetchFeedingSessions(childId, { 
          timeRange, 
          searchQuery 
        });
        
        if (error) {
          console.error('📊 FEEDING SESSIONS ERROR:', error);
          throw error;
        }
        
        console.log(`📊 FEEDING RECORDS COUNT: ${data?.length || 0}`);
        
        if (!data || data.length === 0) {
          console.log('📊 NO FEEDING SESSIONS FOUND for childId:', childId);
          return [];
        }
        
        // Format results for UI consumption with proper type casting
        const results = data.map(record => 
          mapFeedingSessionToActivity(record as unknown as FeedingSessionWithJoins)
        );
        
        return results;
      } catch (error) {
        console.error('📊 ERROR FETCHING FEEDING SESSIONS', error);
        throw error;
      }
    },
    staleTime: 60000, // 1 minute
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...queryOptions
  });
}

/**
 * Map a sleep session from the database to an activity for the UI
 */
export function mapSleepSessionToActivity(session: SleepSessionWithJoins): ActivityType {
  // Calculate duration in minutes
  const startTime = new Date(session.start_time);
  const endTime = new Date(session.end_time);
  const durationMinutes = session.total_duration || 
    Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));
  
  // Extract join fields safely
  const onsetMethod = session.onset_methods?.method || null;
  const soundName = session.sound?.name || null;
  const soundLevel = session.sound_level?.level || null;
  const restfulnessDesc = session.restfulness?.rating || null;
  const locationDesc = session.location?.description || null;
  const environmentDesc = session.environment?.description || null;
  
  console.log('🔄 [activity-service] MAPPING SLEEP SESSION', { 
    id: session.id,
    child_id: session.child_id,
    joins: { 
      onsetMethod, 
      soundName, 
      soundLevel, 
      restfulness: restfulnessDesc,
      location: locationDesc,
      environment: environmentDesc
    } 
  });

  // Create activity object with mapped properties
  return {
    id: session.id,
    type: 'sleep',
    startTime: session.start_time,
    endTime: session.end_time,
    duration: durationMinutes,
    notes: session.notes || '',
    childId: session.child_id,
    caregiver_id: session.caregiver_id,
    quality: session.restfulness_id,
    qualityName: restfulnessDesc,
    location: session.location_id,
    locationName: locationDesc,
    environment: session.environment_id,
    environmentName: environmentDesc,
    onset_method: session.onset_method_id,
    onsetMethodName: onsetMethod,
    sound: session.sound_id,
    soundName: soundName,
    sound_level: session.sound_level_id,
    soundLevelName: soundLevel,
    signs_of_sleep_debt: session.signs_of_sleep_debt
  };
}

/**
 * Map a nap session from the database to an activity for the UI
 */
export function mapNapSessionToActivity(session: NapSessionWithJoins): ActivityType {
  // Extract joined field values with null fallbacks
  const onsetMethodId = session.sleep_onset_method?.id || null;
  const restfulnessId = session.restfulness_rating?.id || null;
  const locationDesc = session.location?.description || null;
  const environmentDesc = session.sleep_environment?.description || null;

  // Calculate duration in minutes
  const durationMinutes = getDurationInMinutes(session.start_time, session.end_time);
  
  // Create the activity object with properties that match the ActivityType interface
  return {
    id: session.id,
    type: 'nap',
    childId: session.child_id,
    startTime: session.start_time,
    endTime: session.end_time,
    duration: durationMinutes,
    notes: session.notes || '',
    caregiver_id: session.caregiver_id,
    quality: session.restfulness_id,
    qualityName: restfulnessId,
    location: session.location_id,
    locationName: locationDesc,
    environment: session.environment_id,
    environmentName: environmentDesc,
    onset_method: session.onset_method_id,
    onsetMethodName: onsetMethodId,
    signs_of_sleep_debt: session.signs_of_sleep_debt,
    sleep_latency: session.sleep_latency
  };
}

/**
 * Map a feeding session from the database to an activity for the UI
 */
export function mapFeedingSessionToActivity(feedingSession: FeedingSessionWithJoins): ActivityType {
  // Calculate duration in minutes
  let durationMinutes = 0;
  try {
    if (feedingSession.start_time && feedingSession.end_time) {
      const start = new Date(feedingSession.start_time);
      const end = new Date(feedingSession.end_time);
      durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
    }
  } catch (error) {
    console.error('Error calculating feeding session duration', error);
  }
  
  console.log('🔄 MAPPING FEEDING SESSION', { 
    id: feedingSession.id,
    child: feedingSession.child,
    type: feedingSession.type
  });
  
  // Extract data based on feeding type
  let amount: number | null = null;
  let unit: string | null = null;
  let temperature: string | null = null;
  let breast: string | null = null;
  let left_duration: number | null = null;
  let right_duration: number | null = null;
  
  // Handle breastfeeding data
  if (feedingSession.type === 'breastfeeding' && feedingSession.breastfeeding && feedingSession.breastfeeding.length > 0) {
    const breastfeedingData = feedingSession.breastfeeding[0];
    left_duration = breastfeedingData.left_duration;
    right_duration = breastfeedingData.right_duration;
    
    // If there's a feeding order, use the first one as the breast
    if (breastfeedingData.feeding_order && breastfeedingData.feeding_order.length > 0) {
      breast = breastfeedingData.feeding_order[0];
    }
  }
  
  // Handle bottle feeding data
  if (feedingSession.type === 'bottle' && feedingSession.bottle_sessions && feedingSession.bottle_sessions.length > 0) {
    const bottleData = feedingSession.bottle_sessions[0];
    amount = bottleData.amount_oz || bottleData.amount_ml / 29.5735; // Convert ml to oz if no oz value
    unit = 'oz';
  }
  
  // Handle formula feeding data
  if (feedingSession.type === 'formula' && feedingSession.formula_sessions && feedingSession.formula_sessions.length > 0) {
    const formulaData = feedingSession.formula_sessions[0];
    amount = formulaData.amount_oz || formulaData.amount_ml / 29.5735; // Convert ml to oz if no oz value
    unit = 'oz';
    temperature = null; // The temperature field isn't in the formula_sessions table
  }
  
  // Create activity object with mapped properties
  return {
    id: feedingSession.id,
    type: 'feeding',
    startTime: formatToLocalTime(feedingSession.start_time),
    endTime: formatToLocalTime(feedingSession.end_time),
    duration: durationMinutes,
    notes: feedingSession.notes || '',
    childId: feedingSession.child_id,
    caregiver_id: feedingSession.caregiver_id,
    feeding_type: feedingSession.type,
    feeding_amount: amount,
    feeding_unit: unit,
    temperature: temperature,
    breast: breast,
    left_duration: left_duration,
    right_duration: right_duration
  };
}

/**
 * Create a custom hook to load activity data
 */
export async function fetchAllActivityData(childId: string, options?: {
  timeRange?: { start: string; end: string };
  searchQuery?: string;
  activityType?: 'sleep' | 'nap' | 'feeding' | 'all';
}): Promise<{
  sleepActivities: ActivityType[];
  napActivities: ActivityType[];
  feedingActivities: ActivityType[];
}> {
  const { timeRange, searchQuery, activityType = 'all' } = options || {};
  
  // Determine which data to fetch based on activityType
  const fetchSleep = activityType === 'all' || activityType === 'sleep';
  const fetchNap = activityType === 'all' || activityType === 'nap';
  const fetchFeeding = activityType === 'all' || activityType === 'feeding';
  
  // Create an array of promises for the data we need
  const promises = [];
  
  if (fetchSleep) {
    promises.push(getChildSleepRecords(childId, undefined, { timeRange, searchQuery }));
  } else {
    promises.push(Promise.resolve([]));
  }
  
  if (fetchNap) {
    promises.push(fetchNapSessions(childId, { timeRange, searchQuery }));
  } else {
    promises.push(Promise.resolve({ data: [] }));
  }
  
  if (fetchFeeding) {
    promises.push(fetchFeedingSessions(childId, { timeRange, searchQuery }));
  } else {
    promises.push(Promise.resolve({ data: [] }));
  }
  
  // Wait for all promises to resolve
  const [sleepResult, napResult, feedingResult] = await Promise.all(promises);
  
  // Process each type of data
  const sleepActivities = processSleepSessions(sleepResult);
  const napActivities = processNapSessions(napResult.data || []);
  const feedingActivities = processFeedingSessions(feedingResult.data || []);
  
  return {
    sleepActivities,
    napActivities,
    feedingActivities
  };
}

/**
 * Fetch activity data combining sleep, nap, and feeding sessions
 */
export async function fetchActivityData(
  childId: string,
  options?: {
    fetchSleep?: boolean;
    fetchNap?: boolean;
    fetchFeeding?: boolean;
    timeRange?: { start: string; end: string };
    searchQuery?: string;
  }
): Promise<ActivityType[]> {
  const {
    fetchSleep = true,
    fetchNap = true,
    fetchFeeding = true,
    timeRange,
    searchQuery
  } = options || {};

  // Log operation
  console.log('🏁 Fetching activity data', {
    childId,
    fetchSleep,
    fetchNap,
    fetchFeeding,
    timeRange
  });

  const promises = [];
  
  if (fetchSleep) {
    // Call the updated getChildSleepRecords with correct parameters
    promises.push(getChildSleepRecords(childId, undefined, { timeRange, searchQuery }));
  } else {
    promises.push(Promise.resolve([]));
  }
  
  if (fetchNap) {
    promises.push(fetchNapSessions(childId, { timeRange, searchQuery }));
  } else {
    promises.push(Promise.resolve({ data: [] }));
  }
  
  if (fetchFeeding) {
    promises.push(fetchFeedingSessions(childId, { timeRange, searchQuery }));
  } else {
    promises.push(Promise.resolve({ data: [] }));
  }
  
  try {
    const [sleepResult, napResult, feedingResult] = await Promise.all(promises);
    
    // Map and combine all results - ensure proper typing for each result
    const activities: ActivityType[] = [
      // Sleep activities
      ...(Array.isArray(sleepResult) ? sleepResult.map(record => mapSleepSessionToActivity(record)) : []),
      
      // Nap activities - ensure proper type casting
      ...(napResult.data ? 
          napResult.data.map(record => mapNapSessionToActivity(record as unknown as NapSessionWithJoins)) 
          : []),
          
      // Feeding activities
      ...(feedingResult.data ? 
          feedingResult.data.map(record => mapFeedingSessionToActivity(record as unknown as FeedingSessionWithJoins)) 
          : [])
    ];
    
    // Sort activities by start time descending
    activities.sort((a, b) => {
      return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
    });
    
    console.log(`🏁 [fetchActivityData] Returning ${activities.length} total activities:`, {
      sleep: sleepResult.length,
      nap: napResult.data?.length || 0,
      feeding: feedingResult.data?.length || 0
    });
    
    return activities;
  } catch (error) {
    console.error('Error fetching activity data:', error);
    throw error;
  }
}

/**
 * Delete an activity by id and type
 */
export async function deleteActivity(id: string, type: 'sleep' | 'nap' | 'feeding') {
  try {
    const tableName = getTableNameByActivityType(type);
    
    console.log(`🗑️ DELETING ${type.toUpperCase()} ACTIVITY`, { id, tableName });
    
    const { error } = await supabase
      .from(tableName as 'sleep_sessions' | 'nap_sessions' | 'feeding_sessions')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error(`🗑️ ERROR DELETING ${type.toUpperCase()} ACTIVITY`, error);
      throw error;
    }
    
    console.log(`🗑️ SUCCESSFULLY DELETED ${type.toUpperCase()} ACTIVITY`, { id });
    return { success: true };
  } catch (error) {
    console.error(`🗑️ EXCEPTION DELETING ${type.toUpperCase()} ACTIVITY`, error);
    throw error;
  }
}

/**
 * Update an activity by id and type
 */
export async function updateActivity(
  id: string, 
  type: 'sleep' | 'nap' | 'feeding', 
  updates: Partial<{ 
    notes: string,
    quality: string | null,
    sound: string | null,
    sound_level: string | null,
    location: string | null,
    environment: string | null,
    onset_method: string | null,
    feeding_type: string | null,
    startTime: string | null, 
    endTime: string | null,   
    duration: number | null,  
    breast: string | null,    
    amount: number | null,    
    unit: string | null,     
    temperature: number | null
  }>
) {
  try {
    const tableName = getTableNameByActivityType(type);
    
    console.log(`✏️ UPDATING ${type.toUpperCase()} ACTIVITY`, { id, tableName, updates });
    
    // Map the generic field names to the specific database column names based on activity type
    const mappedUpdates: Record<string, any> = {};
    
    // Notes field is common to all activity types
    if (updates.notes !== undefined) {
      mappedUpdates.notes = updates.notes;
    }
    
    // Handle timestamp fields - ensuring timezone info is preserved
    if (updates.startTime !== undefined) {
      // Use the string directly - don't convert to UTC
      mappedUpdates.start_time = updates.startTime;
      console.log('Preserving start_time with timezone info:', {
        original: updates.startTime
      });
    }
    
    if (updates.endTime !== undefined) {
      // Use the string directly - don't convert to UTC
      mappedUpdates.end_time = updates.endTime;
      console.log('Preserving end_time with timezone info:', {
        original: updates.endTime
      });
    }
    
    // Handle type-specific field mappings
    if (type === 'sleep' || type === 'nap') {
      // Quality field maps to restfulness_id
      if (updates.quality !== undefined) {
        mappedUpdates.restfulness_id = updates.quality;
      }
      
      // Location field
      if (updates.location !== undefined) {
        mappedUpdates.location_id = updates.location;
      }
      
      // Environment field
      if (updates.environment !== undefined) {
        mappedUpdates.environment_id = updates.environment;
      }
      
      // Onset method field
      if (updates.onset_method !== undefined) {
        mappedUpdates.onset_method_id = updates.onset_method;
      }
    }
    
    // Sleep-specific fields
    if (type === 'sleep') {
      // Sound field
      if (updates.sound !== undefined) {
        mappedUpdates.sound_id = updates.sound;
      }
      
      // Sound level field
      if (updates.sound_level !== undefined) {
        mappedUpdates.sound_level_id = updates.sound_level;
      }
    }
    
    // Feeding-specific fields
    if (type === 'feeding') {
      if (updates.feeding_type !== undefined) {
        mappedUpdates.type = updates.feeding_type;
      }
      
      if (updates.duration !== undefined) {
        mappedUpdates.duration = updates.duration;
      }
      
      if (updates.breast !== undefined) {
        mappedUpdates.breast = updates.breast;
      }
      
      if (updates.amount !== undefined) {
        mappedUpdates.amount = updates.amount;
      }
      
      if (updates.unit !== undefined) {
        mappedUpdates.unit = updates.unit;
      }
      
      // Add support for temperature field
      if ((updates as any).temperature !== undefined) {
        mappedUpdates.temperature = (updates as any).temperature;
      }
    }
    
    console.log(`✏️ MAPPED UPDATES FOR ${type.toUpperCase()} ACTIVITY`, mappedUpdates);
    
    const { error } = await supabase
      .from(tableName as 'sleep_sessions' | 'nap_sessions' | 'feeding_sessions')
      .update(mappedUpdates)
      .eq('id', id);
    
    if (error) {
      console.error(`✏️ ERROR UPDATING ${type.toUpperCase()} ACTIVITY`, error);
      throw error;
    }
    
    console.log(`✏️ SUCCESSFULLY UPDATED ${type.toUpperCase()} ACTIVITY`, { id });
  } catch (error) {
    console.error(`✏️ EXCEPTION UPDATING ${type.toUpperCase()} ACTIVITY`, error);
    throw error;
  }
}

/**
 * Helper to process sleep sessions into activity type
 */
function processSleepSessions(sessions: SleepSessionWithJoins[]): ActivityType[] {
  return sessions.map(mapSleepSessionToActivity);
}

/**
 * Helper to process nap sessions into activity type
 */
function processNapSessions(sessions: NapSessionWithJoins[]): ActivityType[] {
  return sessions.map(mapNapSessionToActivity);
}

/**
 * Helper to process feeding sessions into activity type
 */
function processFeedingSessions(sessions: FeedingSessionWithJoins[]): ActivityType[] {
  return sessions.map(mapFeedingSessionToActivity);
}

function getDurationInMinutes(startTime: string, endTime: string): number {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return Math.round((end.getTime() - start.getTime()) / (1000 * 60));
}

function formatDuration(durationMinutes: number): string {
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  return `${hours} hours ${minutes} minutes`;
}
